import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import employeesCanonical from "@/api/employees-canonical";
import { formatApiError } from "@/api/client";
import { SOURCE_OPTIONS } from "./CreateEmployeeDialog";

/**
 * Batch 2 (Tarea 2) — Modal completo de edición de un canonical.
 *
 * Reemplaza el edit inline previo (que sólo permitía renombrar y quitar
 * aliases). Ahora el admin puede:
 *   1. Renombrar el canonical.
 *   2. Añadir NUEVOS aliases (source + code) o quitar los existentes.
 *   3. Alternar el flag `active` con un Switch — el toggle desactiva sin
 *      borrar; el historial de tx/hours se preserva.
 *
 * El modal usa el mismo endpoint PATCH `/api/employees-canonical/{id}`
 * que ya soporta `active: Optional[bool]` en `CanonicalUpdate`.
 */
export default function EditEmployeeDialog({ open, onOpenChange, row, onSaved }) {
  if (!open || !row) return null;
  return (
    <EditEmployeeDialogBody
      open={open}
      onOpenChange={onOpenChange}
      row={row}
      onSaved={onSaved}
    />
  );
}

function EditEmployeeDialogBody({ open, onOpenChange, row, onSaved }) {
  const { t } = useTranslation();
  const [name, setName] = useState(row.canonical_name || "");
  const [aliases, setAliases] = useState(() =>
    (row.aliases || []).map((a) => ({ code: a.code, source: a.source }))
  );
  const [active, setActive] = useState(row.active !== false);
  const [saving, setSaving] = useState(false);

  // Re-sync when a different row is loaded into the same open modal
  // (defensive — the parent unmounts on close so this rarely fires).
  useEffect(() => {
    setName(row.canonical_name || "");
    setAliases((row.aliases || []).map((a) => ({ code: a.code, source: a.source })));
    setActive(row.active !== false);
  }, [row.id]);

  const cleanedAliases = useMemo(
    () =>
      aliases
        .map((a) => ({ code: (a.code || "").trim(), source: a.source }))
        .filter((a) => a.code && a.source),
    [aliases]
  );

  const canSave = name.trim().length > 0 && !saving;

  const updateAlias = (idx, patch) =>
    setAliases((curr) => curr.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  const addAlias = () =>
    setAliases((curr) => [...curr, { code: "", source: "OPER" }]);
  const removeAlias = (idx) =>
    setAliases((curr) => curr.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        canonical_name: name.trim(),
        aliases: cleanedAliases,
        active,
      };
      await employeesCanonical.update(row.id, payload);
      toast.success(t("employeesCanonical.updated"));
      onOpenChange(false);
      if (typeof onSaved === "function") await onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="edit-employee-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5" />
            {t("employeesCanonical.editTitle", { name: row.canonical_name })}
          </DialogTitle>
          <DialogDescription>
            {t("employeesCanonical.editDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="edit-employee-name" className="text-xs">
              {t("employeesCanonical.createNameLabel")}
            </Label>
            <Input
              id="edit-employee-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="edit-employee-name-input"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">
                {t("employeesCanonical.createAliasesLabel")}
              </Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addAlias}
                data-testid="edit-employee-add-alias"
                className="h-7"
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                {t("employeesCanonical.createAddAlias")}
              </Button>
            </div>

            <div className="space-y-2 rounded-md border p-3">
              {aliases.length === 0 && (
                <p className="text-xs text-amber-500" data-testid="edit-employee-no-aliases-warning">
                  {t("employeesCanonical.editNoAliasesWarning")}
                </p>
              )}
              {aliases.map((a, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2"
                  data-testid={`edit-employee-alias-row-${idx}`}
                >
                  <Select
                    value={a.source}
                    onValueChange={(v) => updateAlias(idx, { source: v })}
                  >
                    <SelectTrigger
                      className="h-9 w-[210px]"
                      data-testid={`edit-employee-alias-source-${idx}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SOURCE_OPTIONS.map((o) => (
                        <SelectItem
                          key={o.value}
                          value={o.value}
                          data-testid={`edit-employee-alias-source-option-${idx}-${o.value}`}
                        >
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={a.code}
                    onChange={(e) => updateAlias(idx, { code: e.target.value })}
                    placeholder={t("employeesCanonical.createAliasCodePlaceholder")}
                    className="h-9 flex-1 font-mono"
                    data-testid={`edit-employee-alias-code-${idx}`}
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => removeAlias(idx)}
                    data-testid={`edit-employee-alias-remove-${idx}`}
                    className="h-9 w-9 text-muted-foreground hover:text-destructive"
                    aria-label={t("common.delete")}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-start justify-between gap-4 rounded-md border p-3">
            <div className="space-y-1">
              <Label htmlFor="edit-employee-active" className="text-xs font-medium">
                {t("employeesCanonical.editActiveLabel")}
              </Label>
              <p className="text-[11px] text-muted-foreground">
                {t("employeesCanonical.editActiveHelp")}
              </p>
            </div>
            <Switch
              id="edit-employee-active"
              checked={active}
              onCheckedChange={setActive}
              data-testid="edit-employee-active-toggle"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="edit-employee-cancel"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={submit}
            disabled={!canSave}
            data-testid="edit-employee-save"
          >
            {saving ? t("common.loading") : t("employeesCanonical.editSave")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
